/**
 * App Versions Management Page
 *
 * Main page for managing app versions with filtering, pagination, and version control.
 * Provides access to version creation, updates, force updates, and soft deletes.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PaginationControls } from '@/components/pagination-controls';
import {
  AppVersionsTable,
  AppVersionFilters,
  AppVersionCreateDialog,
  AppVersionDetailModal,
  AppVersionBulkActions,
} from '@/components/app-versions';
import { useAppVersions } from '@/hooks/use-app-versions';
import type { Platform, AppVersion } from '@/types/app-version';

/**
 * App Versions Page Component
 */
export function Component() {
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Filter state
  const [platformFilter, setPlatformFilter] = useState<Platform | undefined>(undefined);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [isMandatoryFilter, setIsMandatoryFilter] = useState<boolean | undefined>(undefined);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Selection state for bulk operations
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);

  // Calculate offset from page
  const offset = (page - 1) * limit;

  // Fetch app versions with current filters
  const {
    data: versionsData,
    isLoading,
    error,
    refetch,
  } = useAppVersions({
    limit,
    offset,
    platform: platformFilter,
    isActive: isActiveFilter,
    isMandatory: isMandatoryFilter,
  });

  // Extract data
  const versions = versionsData?.data || [];
  const pagination = versionsData?.pagination;

  // Calculate total pages
  const totalPages = pagination ? Math.ceil(pagination.total / limit) : 0;

  /**
   * Handle Version Click
   * Opens detail modal for the selected version
   */
  const handleVersionClick = (version: AppVersion) => {
    setSelectedVersionId(version.id);
  };

  /**
   * Handle Close Modal
   */
  const handleCloseModal = () => {
    setSelectedVersionId(null);
  };

  /**
   * Handle Page Change
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedVersions([]); // Clear selections on page change
  };

  /**
   * Handle Limit Change
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to page 1 when limit changes
    setSelectedVersions([]); // Clear selections on limit change
  };

  /**
   * Handle Filter Change
   */
  const handleFilterChange = (filters: {
    platform?: Platform;
    isActive?: boolean;
    isMandatory?: boolean;
  }) => {
    setPlatformFilter(filters.platform);
    setIsActiveFilter(filters.isActive);
    setIsMandatoryFilter(filters.isMandatory);
    setPage(1); // Reset to page 1 when filters change
    setSelectedVersions([]); // Clear selections on filter change
  };

  /**
   * Handle Clear Filters
   */
  const handleClearFilters = () => {
    setPlatformFilter(undefined);
    setIsActiveFilter(undefined);
    setIsMandatoryFilter(undefined);
    setPage(1);
    setSelectedVersions([]);
  };

  /**
   * Handle Selection Change
   */
  const handleSelectionChange = (ids: string[]) => {
    setSelectedVersions(ids);
  };

  /**
   * Handle Clear Selection
   */
  const handleClearSelection = () => {
    setSelectedVersions([]);
  };

  // Calculate summary statistics
  const activeVersions = versions.filter(v => v.isActive).length;
  const forceUpdatesScheduled = versions.filter(v => v.forceUpdateAfter).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">App Versions</h1>
          <p className="text-muted-foreground mt-2">
            Manage app version requirements, forced updates, and minimum supported versions
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Version
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Versions</CardDescription>
            <CardTitle className="text-4xl">{pagination?.total || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {platformFilter ? `Filtered by ${platformFilter}` : 'All platforms'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Versions</CardDescription>
            <CardTitle className="text-4xl">{activeVersions}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Active on this page</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Force Updates Scheduled</CardDescription>
            <CardTitle className="text-4xl">{forceUpdatesScheduled}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Force updates on this page</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Versions</CardTitle>
          <CardDescription>
            Click on a version to view details and manage its configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <AppVersionFilters
            platform={platformFilter}
            isActive={isActiveFilter}
            isMandatory={isMandatoryFilter}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />

          {/* Bulk Actions Bar */}
          {selectedVersions.length > 0 && (
            <AppVersionBulkActions
              selectedCount={selectedVersions.length}
              selectedIds={selectedVersions}
              onClearSelection={handleClearSelection}
              onSuccess={() => {
                setSelectedVersions([]);
                refetch();
              }}
            />
          )}

          {/* Versions Table */}
          <AppVersionsTable
            versions={versions}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            onVersionClick={handleVersionClick}
            selectedVersions={selectedVersions}
            onSelectionChange={handleSelectionChange}
          />

          {/* Pagination */}
          {pagination && totalPages > 1 && (
            <PaginationControls
              pagination={{
                page,
                limit,
                total: pagination.total,
                totalPages,
              }}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <AppVersionCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          setCreateDialogOpen(false);
          refetch();
        }}
      />

      {/* Detail Modal */}
      <AppVersionDetailModal versionId={selectedVersionId} onClose={handleCloseModal} />
    </div>
  );
}

// Set display name for React Router
Component.displayName = 'AppVersionsPage';
